import { useMutation } from '@apollo/client/react'
import { useTranslation } from 'next-i18next'
import React, { useCallback, useRef, useState } from 'react'
import useLogger from 'src/context/LoggerProvider/useLogger'
import {
  IMutation,
  IMutationRequestDirectoryRemovalArgs
} from 'src/types/graphql'
import { trackDirectoryRemovalRequest } from 'src/utils/pixels/directoryEvents'
import { REQUEST_DIRECTORY_REMOVAL } from './claimQuery'
import { ACCESSIBLE_RED } from './claimTheme'

interface RequestRemovalProps {
  locationId: string
  facilityName: string
}

/** Where a removal lands when the facility has no contact of any kind on record. */
const FALLBACK_EMAIL = 'hola@spotme.mx'

/**
 * "Take my ficha down" (plan §3.6).
 *
 * The removal link is NOT printed on this page, and that is the whole design:
 * a one-click link anyone could see would let a competitor delist a business.
 * Pressing this sends the signed link to the facility's OWN on-record email or
 * phone; only someone with access to that inbox/line can finish the job. The
 * response carries a mask so the operator knows where to look, and nothing more.
 *
 * When we hold no contact at all, the honest answer is the manual route — not
 * a spinner that resolves to nothing.
 */
const RequestRemoval = ({ locationId, facilityName }: RequestRemovalProps) => {
  const { t } = useTranslation('directorio')
  const logger = useLogger()
  const [sentTo, setSentTo] = useState<string | null>(null)
  const [fallback, setFallback] = useState<string | null>(null)
  // Separate from `sentTo`/`fallback`: the server may legitimately answer with
  // BOTH null, and keying the result on `sentTo || fallback` made that case
  // render nothing at all — the button stayed, no message appeared, and the
  // press looked like it had done nothing.
  const [done, setDone] = useState(false)
  const [failed, setFailed] = useState(false)
  const resultRef = useRef<HTMLParagraphElement>(null)

  const [request, { loading }] = useMutation<
    Pick<IMutation, 'requestDirectoryRemoval'>,
    IMutationRequestDirectoryRemovalArgs
  >(REQUEST_DIRECTORY_REMOVAL)

  const onClick = useCallback(async () => {
    setFailed(false)
    try {
      const { data } = await request({ variables: { locationId } })
      const result = data?.requestDirectoryRemoval
      setSentTo(result?.sentTo ?? null)
      setFallback(result?.fallbackEmail ?? null)
      setDone(true)
      trackDirectoryRemovalRequest({
        locationId,
        delivered: !!result?.sentTo
      })
      // The button is replaced by the result, so focus has to follow or the
      // caret drops to <body> and nothing is announced.
      requestAnimationFrame(() => resultRef.current?.focus())
    } catch (error) {
      logger.warn('Directory removal request failed', { error, locationId })
      setFailed(true)
    }
  }, [request, locationId, logger])

  if (done) {
    const mailto = `mailto:${fallback ?? FALLBACK_EMAIL}?subject=${encodeURIComponent(
      `${t('FICHA.REMOVE_SUBJECT')} — ${facilityName}`
    )}`
    return (
      <p
        ref={resultRef}
        tabIndex={-1}
        role="status"
        className="mt-6 max-w-140 text-[12px] leading-relaxed text-ink-200 outline-ink-700 focus-visible:outline-2 focus-visible:outline-offset-2"
      >
        {sentTo ? (
          t('FICHA.REMOVE_SENT', { destination: sentTo })
        ) : (
          <>
            {/* ONE expression for the sentence + its trailing space, never
                `text{' '}<a>`. A `{' '}` spacer between two children emits
                React comment boundaries into the SSR HTML (`text<!-- --> <!--
                --><a>`); comment-stripping mobile webviews remove them and the
                page hard-fails hydration with React 19 #418. Caught by
                `scripts/check-hydration-text.mjs` (Trigger H). */}
            {`${t('FICHA.REMOVE_NO_CONTACT')} `}
            <a
              href={mailto}
              className="underline underline-offset-2 hover:text-ink-700"
            >
              {fallback ?? FALLBACK_EMAIL}
            </a>
          </>
        )}
      </p>
    )
  }

  return (
    <div className="mt-6">
      <button
        type="button"
        onClick={onClick}
        disabled={loading}
        // `min-h-11` + padding: this is a standalone block-level control (its
        // own line, its own help text), so SC 2.5.8's inline-link exception does
        // not apply — and it is the ONLY way a business owner can ask to be
        // delisted.
        className="-mx-1 inline-flex min-h-11 items-center px-1 text-[12px] text-ink-200 underline underline-offset-2 hover:text-ink-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-700 disabled:opacity-60"
      >
        {loading ? t('FICHA.REMOVE_SENDING') : t('FICHA.REMOVE')}
      </button>
      <p className="mt-1 max-w-140 text-[11px] leading-snug text-ink-200">
        {t('FICHA.REMOVE_HELP')}
      </p>
      {failed && (
        <p
          role="alert"
          className="mt-1 text-[11px]"
          style={{ color: ACCESSIBLE_RED }}
        >
          {t('FICHA.REMOVE_FAILED')}
        </p>
      )}
    </div>
  )
}

export default React.memo(RequestRemoval)
